import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useEffect, useState } from "react";
import { SetAlertType } from "schemas/functions";

import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import TemplateBody from "features/Influencer/Templates/SharedComponents/TemplateBody";
import { fetcherAuth } from "utils/api";

interface Props {
  open: boolean;
  handleClose: () => void;
  editTemplateId?: number;
  templateIntakeFormId: number;
  setAlert: SetAlertType;
  fetchUserPurchases: () => Promise<void>;
}

const AddTemplateForUserDialog = ({
  open,
  handleClose,
  editTemplateId,
  templateIntakeFormId,
  setAlert,
  fetchUserPurchases,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();
  const [editTemplate, setEditTemplate] = useState<Template>({
    name: "Outreach (Copywriter)",
    contentType: TemplateContentType.OUTREACH,
    subject: "",
    body: "",
  });

  useEffect(() => {
    if (
      editTemplate?.contentType === TemplateContentType.FOLLOWUP &&
      editTemplate?.name === "Outreach (Copywriter)"
    ) {
      setEditTemplate((prev) => ({
        ...prev,
        name: "Follow Up (Copywriter)",
      }));
    } else if (
      editTemplate?.contentType === TemplateContentType.OUTREACH &&
      editTemplate?.name === "Follow Up (Copywriter)"
    ) {
      setEditTemplate((prev) => ({
        ...prev,
        name: "Outreach (Copywriter)",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplate?.contentType]);

  const createTemplate = async () => {
    setLoading(true);
    try {
      const url = editTemplate?.id
        ? `/api/admin/template-intake-forms/${templateIntakeFormId}/templates/${editTemplate.id}`
        : `/api/admin/template-intake-forms/${templateIntakeFormId}/templates`;
      const { template } = await fetcherAuth(
        getToken,
        url,
        editTemplate?.id ? "PUT" : "POST",
        {},
        {
          body: editTemplate?.body,
          name: editTemplate.name,
          subject: editTemplate.subject,
          contentType: editTemplate.contentType,
        },
      );
      setAlert("Successfully saved.", "success");
      setEditTemplate(template);
      fetchUserPurchases();
      handleClose();
    } catch (error) {
      setAlert(error.message || "Unable to create template", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateForAdmin = async () => {
    try {
      const { template } = await fetcherAuth(
        getToken,
        `/api/admin/template-intake-forms/${templateIntakeFormId}/templates/${editTemplateId}`,
        "GET",
      );
      setEditTemplate(template);
    } catch (error) {
      setAlert(error.message || "Unable to fetch template", "error");
    }
  };

  useEffect(() => {
    if (editTemplateId) {
      fetchTemplateForAdmin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplateId]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Templates For User</DialogTitle>

      <DialogContent>
        <TemplateBody
          editTemplate={editTemplate}
          setEditTemplate={setEditTemplate}
          isNew={!editTemplateId}
          allowChangeTitle={true}
          fromAdmin={true}
          displayError={true}
        />

        <DialogActions>
          <Button color="secondary">Cancel</Button>
          <LoadingButton
            loading={loading}
            variant="contained"
            onClick={createTemplate}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default AddTemplateForUserDialog;
